import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { ReactNode } from 'react';

type Props = {
  referenceId: string;
  status: string;
  amount: string;
  paymentDate: string;
  paymentType: string;
  cardHolderName?: string | null;
  cardHolderEmail?: string | null;
  loanId?: string | null;
  purpose?: string | null;
};

export const APPRAISAL_RECEIPT_PREVIEW_WIDTH = 720;

const useStyles = makeStyles({
  canvasRoot: {
    width: `${APPRAISAL_RECEIPT_PREVIEW_WIDTH}px`,
    background: '#FFFFFF',
    color: '#111827',
    fontFamily: '"Helvetica Neue", Arial, sans-serif',
    overflow: 'hidden',
  },
  topSection: {
    padding: '24px 28px 18px',
    minHeight: '128px',
    position: 'relative',
  },
  topRow: {
    marginBottom: '18px',
  },
  referenceLabel: {
    fontSize: '15px',
    lineHeight: '22px',
    color: '#9CA3AF',
    marginBottom: '4px',
  },
  referenceValue: {
    fontSize: '48px',
    lineHeight: '54px',
    fontWeight: 500,
    color: '#111827',
  },
  statusPill: {
    display: 'inline-block',
    minHeight: '54px',
    padding: '0 22px',
    borderRadius: '999px',
    background: '#DDF9EB',
    border: '1px solid #A7F3D0',
    width: 'fit-content',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  statusText: {
    display: 'inline-block',
    fontSize: '20px',
    lineHeight: '52px',
    fontWeight: 700,
    color: '#047857',
  },
  section: {
    borderTop: '1px solid #E5E7EB',
  },
  sectionHeader: {
    height: '52px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px',
    background: '#F3F6FA',
    borderBottom: '1px solid #E5E7EB',
  },
  sectionTitle: {
    fontSize: '13px',
    lineHeight: '18px',
    fontWeight: 700,
    color: '#4B5563',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  sectionBody: {
    padding: '10px 24px 0',
  },
  amountLabel: {
    fontSize: '20px',
    lineHeight: '28px',
    color: '#374151',
    marginBottom: '6px',
  },
  amountValue: {
    fontSize: '52px',
    lineHeight: '60px',
    fontWeight: 400,
    color: '#111827',
    marginBottom: '10px',
  },
  row: {
    minHeight: '66px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: '1px solid #E5E7EB',
  },
  rowLabel: {
    fontSize: '16px',
    lineHeight: '24px',
    color: '#6B7280',
    paddingRight: '24px',
  },
  rowValue: {
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 500,
    color: '#111827',
    textAlign: 'right',
    wordBreak: 'break-word',
    maxWidth: '58%',
  },
  emailValue: {
    color: '#2563EB',
  },
});

const fallback = (value?: string | null) =>
  value && value.trim() ? value : '-';

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>{title}</Typography>
      </Box>
      <Box className={classes.sectionBody}>{children}</Box>
    </Box>
  );
};

const DataRow = ({
  label,
  value,
  isEmail = false,
}: {
  label: string;
  value?: string | null;
  isEmail?: boolean;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.row}>
      <Typography className={classes.rowLabel}>{label}</Typography>
      <Typography
        className={`${classes.rowValue} ${isEmail ? classes.emailValue : ''}`}
      >
        {fallback(value)}
      </Typography>
    </Box>
  );
};

export const AppraisalPaymentReceiptPreview = ({
  referenceId,
  status,
  amount,
  paymentDate,
  paymentType,
  cardHolderName,
  cardHolderEmail,
  loanId,
  purpose,
}: Props) => {
  const classes = useStyles();

  return (
    <Box className={classes.canvasRoot}>
      <Box className={classes.topSection}>
        <Box className={classes.topRow}>
          <Typography className={classes.referenceLabel}>Ref #:</Typography>
          <Typography className={classes.referenceValue}>
            {referenceId}
          </Typography>
        </Box>

        <Box className={classes.statusPill}>
          <span className={classes.statusText}>{`\u25CF ${fallback(
            status
          )}`}</span>
        </Box>
      </Box>

      <Section title="General Information">
        <Typography className={classes.amountLabel}>Amount:</Typography>
        <Typography className={classes.amountValue}>{amount}</Typography>
        <DataRow label="Transaction type:" value="Sale" />
        <DataRow label="Payment type:" value={paymentType} />
        <DataRow label="Entered date:" value={paymentDate} />
      </Section>

      <Section title="Cardholder Information">
        <DataRow label="Name:" value={cardHolderName} />
        <DataRow label="Email:" value={cardHolderEmail} isEmail />
      </Section>

      <Section title="Custom Information">
        <DataRow label="ID" value={loanId} />
        <DataRow label="Payment type" value={purpose} />
      </Section>
    </Box>
  );
};
